import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import './BraspressConfigModal.css';

const CNPJ_LEN = 14;
const MAX_CONTAS = 30;

function authHeaders(token) {
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}

function newKey() {
  return `k-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function emptyConta() {
  return {
    key: newKey(),
    id: null,
    cnpj: '',
    usuario: '',
    senha: '',
    senhaCadastrada: false
  };
}

function mapApiConta(c) {
  return {
    key: newKey(),
    id: c.id != null ? Number(c.id) : null,
    cnpj: String(c.cnpj || ''),
    usuario: String(c.usuario || ''),
    senha: '',
    senhaCadastrada: !!c.senhaCadastrada
  };
}

export default function BraspressConfigModal({ open, onClose, apiBaseUrl, onSaved }) {
  const [contas, setContas] = useState([emptyConta()]);
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [justSaved, setJustSaved] = useState(false);
  const [testIdRegistro, setTestIdRegistro] = useState('');
  const [testNota, setTestNota] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testMsg, setTestMsg] = useState(null);
  const [testOk, setTestOk] = useState(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setLoading(true);
    setSaveError('');
    try {
      const res = await fetch(`${apiBaseUrl}/api/integracoes/braspress`, {
        headers: authHeaders(token)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        setSaveError(json.message || 'Erro ao carregar dados.');
        return;
      }
      const d = json.data || {};
      const list = Array.isArray(d.contas) ? d.contas : [];
      setContas(list.length ? list.map(mapApiConta) : [emptyConta()]);
      setTestMsg(null);
      setTestOk(null);
      setTestNota('');
      setTestIdRegistro('');
    } catch (e) {
      setSaveError(e.message || 'Erro de rede.');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    if (!open) return;
    setJustSaved(false);
    load();
  }, [open, load]);

  const onChangeCnpj = (key, value) => {
    const v = value.replace(/[^A-Za-z0-9]/g, '').slice(0, CNPJ_LEN);
    setContas((prev) => prev.map((r) => (r.key === key ? { ...r, cnpj: v } : r)));
  };

  const onChangeTestNota = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
    setTestNota(digits);
  };

  const addConta = () => {
    setContas((prev) => (prev.length >= MAX_CONTAS ? prev : [...prev, emptyConta()]));
  };

  const removeConta = (key) => {
    setContas((prev) => {
      const next = prev.filter((r) => r.key !== key);
      return next.length ? next : [emptyConta()];
    });
  };

  const handleSave = async () => {
    const rowsToSave = contas.filter(
      (r) => r.cnpj.trim() || r.usuario.trim() || String(r.senha || '').trim()
    );
    const payload = rowsToSave.map((rowRef) => ({
      id: rowRef.id != null && rowRef.id > 0 ? rowRef.id : undefined,
      cnpj: rowRef.cnpj.trim(),
      usuario: rowRef.usuario.trim(),
      senha: rowRef.senha
    }));

    for (let i = 0; i < payload.length; i += 1) {
      const r = payload[i];
      const rowRef = rowsToSave[i];
      if (r.cnpj.length !== CNPJ_LEN) {
        setSaveError(`CNPJ inválido na credencial ${i + 1} (14 caracteres alfanuméricos).`);
        return;
      }
      if (!r.usuario) {
        setSaveError(`Usuário obrigatório na credencial ${i + 1}.`);
        return;
      }
      if (!r.id && !String(r.senha || '').trim()) {
        setSaveError(`Senha obrigatória na credencial ${i + 1} (nova).`);
        return;
      }
      if (r.id && !String(r.senha || '').trim() && !rowRef.senhaCadastrada) {
        setSaveError(`Senha obrigatória na credencial ${i + 1}.`);
        return;
      }
    }
    setSaveError('');

    const token = localStorage.getItem('authToken');
    if (!token) return;
    setJustSaved(false);
    setLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/integracoes/braspress`, {
        method: 'PUT',
        headers: authHeaders(token),
        body: JSON.stringify({ contas: payload })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        setSaveError(json.message || 'Erro ao salvar.');
        return;
      }
      await load();
      setJustSaved(true);
      if (typeof onSaved === 'function') onSaved();
    } catch (e) {
      setSaveError(e.message || 'Erro de rede.');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setTestMsg(null);
    setTestOk(null);
    setTestLoading(true);
    try {
      const idRegistro = testIdRegistro ? parseInt(testIdRegistro, 10) : null;
      const res = await fetch(`${apiBaseUrl}/api/integracoes/braspress/test`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          notaFiscal: testNota,
          idRegistro
        })
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        setTestOk(true);
        setTestMsg('Sucesso no teste.');
        return;
      }
      setTestOk(false);
      setTestMsg(json.message || `Erro HTTP ${res.status}.`);
    } catch (e) {
      setTestOk(false);
      setTestMsg(e.message || 'Erro de rede.');
    } finally {
      setTestLoading(false);
    }
  };

  if (!open) return null;

  const savedForTest = contas.filter(
    (c) => c.id && c.senhaCadastrada && c.cnpj.length === CNPJ_LEN && c.usuario.trim()
  );
  const canShowTest = savedForTest.length > 0;
  const canRunTest =
    canShowTest &&
    String(testIdRegistro || '').trim() !== '' &&
    testNota.replace(/\D/g, '').length > 0;

  return (
    <div className="braspress-modal-overlay">
      <div
        className="braspress-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="braspress-modal-title"
      >
        <div className="braspress-modal-header">
          <div>
            <h2 id="braspress-modal-title" className="braspress-modal-title">
              Braspress
            </h2>
            <p className="braspress-modal-sub">Rastreio de encomendas — credenciais da API Braspress (vários CNPJs).</p>
          </div>
          <button type="button" className="braspress-modal-close" onClick={onClose} aria-label="Fechar">
            <X size={20} />
          </button>
        </div>
        <div className="braspress-modal-body">
          {justSaved && (
            <div className="braspress-saved-banner">Dados salvos com sucesso.</div>
          )}
          {saveError && (
            <div className="braspress-test-msg error" style={{ marginBottom: 12 }}>
              {saveError}
            </div>
          )}

          <div className="braspress-contas-toolbar">
            <span className="braspress-contas-hint">Credenciais por CNPJ</span>
            <button
              type="button"
              className="braspress-btn-add"
              onClick={addConta}
              disabled={loading || contas.length >= MAX_CONTAS}
            >
              <Plus size={12} strokeWidth={2} aria-hidden />
              Adicionar CNPJ
            </button>
          </div>

          <div className="braspress-contas-scroll">
            {contas.map((row, idx) => (
              <div key={row.key} className="braspress-conta-card">
                <div className="braspress-conta-card-head">
                  <span className="braspress-conta-n">Credencial {idx + 1}</span>
                  <button
                    type="button"
                    className="braspress-conta-remove"
                    onClick={() => removeConta(row.key)}
                    disabled={loading}
                    aria-label={`Remover credencial ${idx + 1}`}
                  >
                    <Trash2 size={12} strokeWidth={2} />
                  </button>
                </div>
                <div className="braspress-conta-fields-row">
                  <div className="braspress-field">
                    <label htmlFor={`braspress-cnpj-${row.key}`}>CNPJ (14 caracteres)</label>
                    <input
                      id={`braspress-cnpj-${row.key}`}
                      type="text"
                      autoComplete="off"
                      maxLength={CNPJ_LEN}
                      value={row.cnpj}
                      onChange={(e) => onChangeCnpj(row.key, e.target.value)}
                      placeholder="Somente letras e números"
                      disabled={loading}
                    />
                  </div>
                  <div className="braspress-field">
                    <label htmlFor={`braspress-usuario-${row.key}`}>Usuário</label>
                    <input
                      id={`braspress-usuario-${row.key}`}
                      type="text"
                      autoComplete="username"
                      value={row.usuario}
                      onChange={(e) =>
                        setContas((prev) =>
                          prev.map((r) => (r.key === row.key ? { ...r, usuario: e.target.value } : r))
                        )
                      }
                      disabled={loading}
                    />
                  </div>
                  <div className="braspress-field">
                    <label htmlFor={`braspress-senha-${row.key}`}>Senha</label>
                    {row.senhaCadastrada && !row.senha ? (
                      <div className="braspress-senha-maskline" aria-hidden>
                        ••••••••
                      </div>
                    ) : null}
                    <input
                      id={`braspress-senha-${row.key}`}
                      type="password"
                      autoComplete="new-password"
                      value={row.senha}
                      onChange={(e) =>
                        setContas((prev) =>
                          prev.map((r) => (r.key === row.key ? { ...r, senha: e.target.value } : r))
                        )
                      }
                      placeholder={
                        row.senhaCadastrada ? 'Deixe em branco para manter a senha atual' : ''
                      }
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="braspress-actions">
            <button
              type="button"
              className="braspress-btn braspress-btn-primary"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Aguarde…' : 'Salvar'}
            </button>
          </div>

          {canShowTest && (
            <div className="braspress-test-block">
              <h3 className="braspress-test-title">Testar integração</h3>
              <p className="braspress-test-hint">
                Escolha o CNPJ (credencial salva), depois informe a nota fiscal (até 6 dígitos).
              </p>
              <div className="braspress-test-grid">
                <div className="braspress-field">
                  <label htmlFor="braspress-test-cnpj">CNPJ / credencial</label>
                  <select
                    id="braspress-test-cnpj"
                    value={testIdRegistro}
                    onChange={(e) => setTestIdRegistro(e.target.value)}
                    disabled={testLoading}
                  >
                    <option value="">Selecione…</option>
                    {savedForTest.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.cnpj}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="braspress-field">
                  <label htmlFor="braspress-nota">Nota fiscal</label>
                  <input
                    id="braspress-nota"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={testNota}
                    onChange={onChangeTestNota}
                    disabled={testLoading}
                  />
                </div>
                <div className="braspress-test-btn-wrap">
                  <button
                    type="button"
                    className="braspress-btn-test"
                    onClick={handleTest}
                    disabled={testLoading || !canRunTest}
                  >
                    Testar
                  </button>
                </div>
              </div>
              {testLoading && (
                <div className="braspress-test-spinner-wrap" aria-live="polite">
                  <div className="braspress-test-spinner" />
                </div>
              )}
              {!testLoading && testMsg && (
                <div className={`braspress-test-msg ${testOk ? 'success' : 'error'}`}>{testMsg}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
